import {
  Alert,
  Box,
  CircularProgress,
  Typography
} from '@mui/material';
import { forwardRef, useImperativeHandle } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useSimpleChat } from '../hooks/useSimpleChat';
import ChatBottomNav from './chat/ChatBottomNav';
import ChatComposer from './chat/ChatComposer';
import ChatMessageBubble from './chat/ChatMessageBubble';

interface Message {
  id: string;
  role: 'user' | 'assistant';
  content: string;
  created_at: string;
}

interface SimpleChatProps {
  onConversationStateChange?: (isClosed: boolean) => void;
  conversationId?: string;
}

const SimpleChat = forwardRef<{ handleCloseConversation: () => Promise<void> }, SimpleChatProps>(({ onConversationStateChange, conversationId }, ref) => {
  const { user } = useAuth();

  const {
    conversation,
    messages,
    inputMessage,
    loading,
    initializing,
    error,
    setError,
    isConversationClosed,
    ttsEnabled,
    setTtsEnabled,
    setInputMessage,
    handleKeyPress,
    sendMessageContent,
    handleSendMessage,
    handleCloseConversation,
    handleStartNewChat,
    messagesEndRef,
    speech,
  } = useSimpleChat({ onConversationStateChange, conversationId });

  const {
    startListening,
    stopListening,
    speak,
    stopSpeaking,
    isListening,
    isSpeaking,
    error: speechError,
    clearError: clearSpeechError,
    supported,
  } = speech;

  useImperativeHandle(ref, () => ({
    handleCloseConversation,
  }));

  const renderMessage = (message: Message) => {
    return (
      <Box
        key={message.id}
      >
        <ChatMessageBubble
          content={message.content}
          createdAt={message.created_at}
          role={message.role}
        />
      </Box>
    );
  };

  if (initializing) {
    return (
      <Box sx={{
        height: '100%',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center'
      }}>
        <CircularProgress />
        <Typography variant="h6" sx={{ ml: 2 }}>
          Starting your chat session...
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>

      {/* Error Display */}
      {error && (
        <Alert severity="error" sx={{ m: 2 }}>
          {error}
        </Alert>
      )}

      {/* Speech Error Display */}
      {speechError && (
        <Alert severity="warning" sx={{ m: 2 }} onClose={clearSpeechError}>
          {speechError}
        </Alert>
      )}

      {/* Messages - Scrollable area */}
      <Box
        sx={{
          flex: 1,
          overflow: 'auto',
          bgcolor: '#FFFFFF',
          minHeight: 0,
          pt: 2,
        }}
      >
        <Box sx={{ pb: 4 }}>
          {messages.map(renderMessage)}

          {loading && (
            <Box
              sx={{
                width: '100%',
                display: 'flex',
                justifyContent: 'flex-start',
                px: 4,
                py: 1,
              }}
            >
              <Box
                sx={{
                  position: 'relative',
                  bgcolor: 'rgba(255, 212, 0, 0.35)',
                  borderRadius: '25px',
                  px: 2.25,
                  py: 1.75,
                  '&:after': {
                    content: '""',
                    position: 'absolute',
                    width: 18,
                    height: 18,
                    bottom: -14,
                    left: -14,
                    borderRadius: '50%',
                    bgcolor: 'rgba(255, 212, 0, 0.5)',
                  },
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <CircularProgress size={16} sx={{ mr: 1, color: '#828282' }} />
                  <Typography
                    variant="body2"
                    sx={{ fontStyle: 'italic', color: '#828282' }}
                  >
                    Jeff is thinking...
                  </Typography>
                </Box>
              </Box>
            </Box>
          )}
          <div ref={messagesEndRef} />
        </Box>
      </Box>

      {isConversationClosed ? (
        <Box sx={{ textAlign: 'center', py: 2, px: 2 }}>
          <Typography variant="body2" color="text.secondary">
            This conversation has ended. You can start a new conversation by refreshing the page.
          </Typography>
        </Box>
      ) : (
        <ChatComposer
          disabled={loading || isConversationClosed || isListening}
          inputMessage={inputMessage}
          isListening={isListening}
          ttsEnabled={ttsEnabled}
          onChange={setInputMessage}
          onKeyPress={handleKeyPress}
          onSend={handleSendMessage}
          onStartNewChat={handleStartNewChat}
          onToggleTts={
            supported.tts
              ? () => {
                const newState = !ttsEnabled;
                setTtsEnabled(newState);
                if (newState && messages.length > 0) {
                  const lastMessage = messages[messages.length - 1];
                  if (lastMessage.role === 'assistant') {
                    const voiceGender =
                      (user?.voice_gender as 'male' | 'female') || 'male';
                    const voiceLanguage = user?.voice_language || 'en-GB';
                    speak(lastMessage.content, undefined, voiceGender, voiceLanguage);
                  }
                } else if (!newState && isSpeaking) {
                  stopSpeaking();
                }
              }
              : undefined
          }
          onToggleListening={
            supported.stt
              ? () => {
                if (isSpeaking) {
                  stopSpeaking();
                }

                if (isListening) {
                  stopListening();
                  return;
                }

                const userLanguage = user?.voice_language || 'en-US';
                startListening(async (text) => {
                  if (!text.trim()) {
                    return;
                  }

                  if (!conversation?.id) {
                    setError(
                      'Conversation not initialized. Please refresh the page.',
                    );
                    return;
                  }

                  try {
                    setInputMessage('');
                    await sendMessageContent(text);
                  } catch (speechSendError: any) {
                    setError('Failed to send message. Please try again.');
                  }
                }, userLanguage);
              }
              : undefined
          }
        />
      )}

      <ChatBottomNav />
    </Box>
  );
});

export default SimpleChat;
